import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { trackFunnel } from '../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileCheck2, Sparkles, Check, Shield, Loader2 } from 'lucide-react';

// Production-grade validation helpers
const validateEmail = (email) => {
  const trimmed = email.trim();
  if (!trimmed) return 'Email is required';
  // RFC 5322 compliant email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!emailRegex.test(trimmed)) return 'Please enter a valid email address';
  return '';
};

const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/\d/.test(password)) return 'Password must contain at least one number';
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(password)) return 'Password must contain at least one special character';
  return '';
};

const validateWorkspaceName = (name) => {
  const trimmed = name.trim();
  if (!trimmed) return 'Workspace name is required';
  if (trimmed.length < 2) return 'Workspace name must be at least 2 characters';
  return '';
};

export const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [signupSource, setSignupSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false, workspace: false });

  // Funnel: signup_started — fire once when user starts typing (any field)
  const [signupStartedFired, setSignupStartedFired] = useState(false);
  useEffect(() => {
    if (!signupStartedFired && (email || password || workspaceName)) {
      trackFunnel('signup_started');
      setSignupStartedFired(true);
    }
  }, [email, password, workspaceName, signupStartedFired]);

  // Validation errors
  const errors = useMemo(() => ({
    email: touched.email ? validateEmail(email) : '',
    password: touched.password ? validatePassword(password) : '',
    workspace: touched.workspace ? validateWorkspaceName(workspaceName) : '',
  }), [email, password, workspaceName, touched]);

  // Form validity
  const isFormValid = useMemo(() => {
    return !validateEmail(email) && !validatePassword(password) && !validateWorkspaceName(workspaceName);
  }, [email, password, workspaceName]);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true, workspace: true });
    
    if (!isFormValid) return;
    
    setLoading(true);

    try {
      await signup(email.trim(), password, workspaceName.trim(), signupSource);
      toast.success('Account created successfully! Please check your email.');
      navigate('/dashboard');
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.detail) {
        // Handle validation errors from backend
        if (Array.isArray(errorData.detail)) {
          errorData.detail.forEach(err => toast.error(err.msg || err.message || 'Validation error'));
        } else {
          toast.error(errorData.detail);
        }
      } else {
        toast.error('Signup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Detect requirement gaps',
    'Generate test scenarios and test cases',
    'Build traceability matrices automatically',
    'Prepare release-ready QA documentation',
  ];

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-slate-50">
        <Card className="w-full max-w-md p-8 sm:p-10 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border-0">
          {/* Logo and Title */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-2.5 mb-4">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl">
                <FileCheck2 className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight">
                TestGenie AI
              </h1>
            </div>
            <p className="text-slate-500 text-sm sm:text-base">
              Start generating QA documentation in minutes
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label 
                htmlFor="workspace" 
                className="text-sm font-medium text-slate-700"
                data-testid="signup-workspace-label"
              >
                Workspace Name
              </Label>
              <Input
                id="workspace"
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                onBlur={() => handleBlur('workspace')}
                placeholder="Your Company Name"
                className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${
                  errors.workspace 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                } focus:ring-4`}
                data-testid="signup-workspace-input"
              />
              {errors.workspace && (
                <p className="text-sm text-red-500 mt-1" data-testid="signup-workspace-error">{errors.workspace}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="email" 
                className="text-sm font-medium text-slate-700"
                data-testid="signup-email-label"
              >
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="you@company.com"
                className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${
                  errors.email 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                } focus:ring-4`}
                data-testid="signup-email-input"
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1" data-testid="signup-email-error">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="password" 
                className="text-sm font-medium text-slate-700"
                data-testid="signup-password-label"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur('password')}
                placeholder="Create a strong password"
                className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${
                  errors.password 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                } focus:ring-4`}
                data-testid="signup-password-input"
              />
              {errors.password && (
                <p className="text-sm text-red-500 mt-1" data-testid="signup-password-error">{errors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="source" className="text-sm font-medium text-slate-700">How did you hear about us? <span className="text-slate-400 font-normal">(optional)</span></Label>
              <select
                id="source"
                value={signupSource}
                onChange={(e) => setSignupSource(e.target.value)}
                className="flex h-12 w-full rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 focus:outline-none transition-all"
                data-testid="signup-source-select"
              >
                <option value="">Select...</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Reddit">Reddit</option>
                <option value="Google">Google Search</option>
                <option value="Friend">Friend / Colleague</option>
                <option value="QA Community">QA Community</option>
                <option value="YouTube">YouTube</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <Button
              type="submit"
              disabled={loading || !isFormValid}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-slate-900/25 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="signup-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Trust Line */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
            <Shield className="h-3.5 w-3.5" />
            <span>Your data is secure and isolated per project</span>
          </div>

          {/* Sign In Link */}
          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </Card>
      </div>

      {/* Right Side - Hero */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        
        {/* Gradient orbs for depth */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          {/* Icon */}
          <div className="mb-8">
            <div className="inline-flex p-3 bg-white/10 backdrop-blur-sm rounded-2xl">
              <Sparkles className="h-10 w-10 text-blue-400" />
            </div>
          </div>
          
          {/* Title */}
          <h2 className="text-3xl xl:text-4xl font-heading font-bold text-white mb-4 tracking-tight leading-tight">
            AI-Powered QA Documentation Platform
          </h2>
          
          {/* Subtitle */}
          <p className="text-lg text-blue-100/80 mb-10 max-w-md leading-relaxed">
            Generate test cases, test plans, and execute QA workflows — all in one intelligent platform.
          </p>
          
          {/* Feature List */}
          <ul className="space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                </div>
                <span className="text-blue-50/90 text-base">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Bottom Stats or Social Proof */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-sm text-blue-200/60">
              Trusted by QA teams at startups and enterprises
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};